import i18next from "src/i18n/config";

import { DeviceState } from "@devices/types";
import { FileInfo } from "@files/types";
import { hasAnalyzerInProgress, hasApkMetadata, hasFailedAnalyzer, isApk } from "@files/utils";

type ApkFileStatus =
    | "toInstall"
    | "toUpdate"
    | "notApk"
    | "installed"
    | "invalidApk"
    | "processing"
    | "analyzerFailed"
    | "currentVersion"
    | "outdatedVersion";

export const getPrimaryButtonProps = (
    status: ApkFileStatus,
    unsuccesfulAnalyzerExist: boolean,
    mayInstallApp: boolean,
    version?: string,
) => {
    switch (status) {
        case "processing":
            return {
                tooltip: i18next.t("sidepanels:devicePanel.tooltip.processing"),
                iconClass: "fas fa-spinner fa-spin",
                disabled: true,
            };
        case "analyzerFailed":
            return {
                tooltip: i18next.t("sidepanels:devicePanel.tooltip.analysisFailed"),
                iconClass: "fa-solid fa-triangle-exclamation",
                disabled: true,
            };
        case "toUpdate":
            return {
                tooltip: mayInstallApp
                    ? version
                        ? i18next.t("sidepanels:devicePanel.tooltip.updateApplication", { version })
                        : i18next.t("sidepanels:devicePanel.tooltip.updateApplicationFallback")
                    : i18next.t("sidepanels:devicePanel.tooltip.noPermissionToUpdatePackages"),
                disabled: unsuccesfulAnalyzerExist || !mayInstallApp,
                iconClass: "fa-solid fa-angles-up",
            };
        case "toInstall":
            return {
                tooltip: mayInstallApp
                    ? i18next.t("sidepanels:devicePanel.tooltip.installApplication")
                    : i18next.t("sidepanels:devicePanel.tooltip.noPermissionToInstallPackages"),
                disabled: unsuccesfulAnalyzerExist || !mayInstallApp,
                iconClass: "fa-solid fa-arrow-right-to-bracket",
            };
        case "invalidApk":
            return {
                tooltip: i18next.t("sidepanels:devicePanel.tooltip.invalidApplication"),
                disabled: true,
                iconClass: "fa-solid fa-arrow-right-to-bracket",
            };
        case "currentVersion":
            return {
                tooltip: i18next.t("sidepanels:devicePanel.tooltip.currentVersion"),
                disabled: true,
                iconClass: "fa-solid fa-check",
            };
        default:
            return {
                tooltip: i18next.t("sidepanels:devicePanel.tooltip.outdatedVersion"),
                disabled: true,
                iconClass: "fa-solid fa-angles-down",
            };
    }
};

export const getApkFileStatus = (file: FileInfo, packages?: DeviceState["packages"] | null): ApkFileStatus => {
    const isApkFile = isApk(file.filename);
    const installedPackage = packages?.find(
        (item) => item.package.toLowerCase() === file.metadata?.file?.apk?.manifest?.package.toLowerCase(),
    );

    const currentAppVersion = Number(installedPackage?.versionCode);
    const appVersionToInstall = Number(file.metadata?.file?.apk?.manifest?.versioncode);
    const analyzerInProgress = hasAnalyzerInProgress(file);
    const analyzerFailed = hasFailedAnalyzer(file);
    const apkMetadataExist = hasApkMetadata(file);
    const isSameVersion = currentAppVersion === appVersionToInstall;

    if (analyzerInProgress) {
        return "processing";
    }
    if (analyzerFailed) {
        return "analyzerFailed";
    }
    if (!isApkFile) {
        return "notApk";
    }
    if (!apkMetadataExist) {
        return "invalidApk";
    }
    if (!installedPackage) {
        return "toInstall";
    }
    if (currentAppVersion < appVersionToInstall) {
        return "toUpdate";
    }
    if (isSameVersion) {
        return "currentVersion";
    }

    return "outdatedVersion";
};
